import { UnmoderatedBooking } from 'containers/Studies/data/UnmoderatedBooking.query';
import { SessionTeamFields } from 'containers/Studies/Schedule/team/data/SessionTeam.query';

import type { RateParticipant } from 'containers/Booking/containers/BookingParticipantsContainer/components/BookingParticipantsSidePanel/queries/RateParticipant.mutation';
import type { UpdateTaskBlockFollowUps } from 'containers/Studies/BuildStudy/Unmoderated/data/UpdateTaskBlockFollowUps.mutation';
import type { UpdateStudyRecordingSettings } from 'containers/Studies/data/UpdateStudyRecordingSettings.mutation';
import type { UpdateSessionTeam } from 'containers/Studies/Schedule/team/data/SessionTeam.query';
import type { UpdateBooking } from 'data/mutations/booking/updateBooking.mutation';

import type { Cache, Variables } from '@urql/exchange-graphcache';
import type { VariablesOf } from 'gql.tada';

export const optimistic = {
  submissionRateParticipant: (args: Variables) => {
    const input = args?.input as VariablesOf<typeof RateParticipant>['input'];
    return {
      __typename: 'Submission',
      _id: input?._submission_id,
      rating: {
        __typename: 'BookingSubmissionRating',
        overall: input?.overall,
      },
    };
  },
  /**
   * A sequence with entries switches follow-ups on. That state locks the task education screen on
   * and limits the study to desktop. The switch, the lock and the sidebar group read the sequence
   * from the cache, thus they must change on the click, not on the response.
   *
   * An empty sequence switches follow-ups off. A group with no members loses nothing, thus it
   * closes on the click too. A group with members detaches them and deletes the sub-tasks among
   * them. The client cannot predict that list, thus it waits for the response.
   */
  updateTaskBlockFollowUps: (args: Variables, cache: Cache) => {
    const input = args.input as VariablesOf<typeof UpdateTaskBlockFollowUps>['input'];
    const followUpIds = input._follow_up_ids ?? [];

    const data = cache.readQuery({ query: UnmoderatedBooking, variables: { id: input._id } });
    const taskBlocks = data?.unmoderatedBooking?.config?.unmoderated?.task_blocks ?? [];
    const parent = taskBlocks.find(block => block?._id === input._follow_up_parent_id);

    // Only a live website test holds a sequence, and only a cached parent can change.
    if (parent?.__typename !== 'TaskBlockLiveWebsiteTest') {
      return null;
    }

    // The sequence leads with the parent's own id. Any other entry is a member.
    const hasMembers = (parent._follow_up_ids ?? []).some(id => id !== parent._id);

    if (followUpIds.length === 0 && hasMembers) {
      return null;
    }

    return {
      __typename: 'Booking' as const,
      _id: input._id,
      config: {
        __typename: 'BookingConfig' as const,
        unmoderated: {
          __typename: 'UnmoderatedBookingConfig' as const,
          // The whole list. A partial list replaces the cached one.
          task_blocks: taskBlocks.map(block =>
            block?._id === parent._id ? { ...parent, _follow_up_ids: followUpIds } : block,
          ),
        },
      },
    };
  },
  updateStudyRecordingSettings: (args: Variables, cache: Cache) => {
    const input = args.input as VariablesOf<typeof UpdateStudyRecordingSettings>['input'];
    const data = cache.readQuery({ query: UnmoderatedBooking, variables: { id: input._id } });
    const taskBlocks = data?.unmoderatedBooking?.config?.unmoderated?.task_blocks ?? [];

    return {
      __typename: 'Booking' as const,
      _id: input._id,
      config: {
        __typename: 'BookingConfig' as const,
        unmoderated: {
          __typename: 'UnmoderatedBookingConfig' as const,
          recording_options: {
            __typename: 'TaskBlockRecordingOptions' as const,
            camera: input.recording_options.camera,
            microphone: input.recording_options.microphone,
            screen: input.recording_options.screen,
          },
          task_blocks: taskBlocks.map(block => {
            if (!block) return block;
            return {
              ...block,
              recording_options: block.recording_options
                ? {
                    __typename: 'TaskBlockRecordingOptions' as const,
                    camera: input.recording_options.camera,
                    microphone: input.recording_options.microphone,
                    screen: input.recording_options.screen,
                  }
                : block.recording_options,
            };
          }),
        },
      },
    };
  },
  /** The Schedule sidebar's session team. Only the server knows a new moderator's calendars. */
  updateBookingConfig: (args: Variables, cache: Cache) => {
    const { _id, input } = args as VariablesOf<typeof UpdateSessionTeam>;
    const moderators = input.session?.moderators;
    const observers = input.session?.observers;
    if (!moderators && !observers) {
      return null;
    }
    const cachedModerators =
      cache.readFragment(SessionTeamFields, { __typename: 'Booking', _id })?.config?.session?.moderators ?? [];

    return {
      __typename: 'Booking' as const,
      _id,
      config: {
        __typename: 'BookingConfig' as const,
        session: {
          __typename: 'SessionBookingConfig' as const,
          ...(moderators && {
            moderators: moderators.map(moderator => ({
              __typename: 'BookingModerator' as const,
              _user_id: moderator._user_id,
              allow_booking_over_events: moderator.allow_booking_over_events,
              calendars: cachedModerators.find(cached => cached._user_id === moderator._user_id)?.calendars ?? [],
            })),
          }),
          ...(observers && {
            observers: observers.map(observer => ({
              __typename: 'BookingObserver' as const,
              _user_id: '_user_id' in observer ? observer._user_id : null,
              email: 'email' in observer ? observer.email : null,
            })),
          }),
        },
      },
    };
  },
  /** The Schedule sidebar's session length and timezone. Other config fields wait for the response. */
  updateBooking: (args: Variables) => {
    const { booking_id, booking } = args as VariablesOf<typeof UpdateBooking>;
    const timezone = booking.config?.timezone;
    const session = booking.config?.session;
    if (timezone === undefined && session?.duration === undefined) {
      return null;
    }

    return {
      __typename: 'Booking' as const,
      _id: booking_id,
      config: {
        __typename: 'BookingConfig' as const,
        ...(timezone !== undefined && { timezone }),
        ...(session?.duration !== undefined && {
          session: {
            __typename: 'SessionBookingConfig' as const,
            duration: session.duration,
            ...(session.time_limit !== undefined && { time_limit: session.time_limit }),
          },
        }),
      },
    };
  },
};
